import React, { useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { SiteSearchCombobox } from './SiteSearchCombobox';
import {
  buildIntraSearchIndex,
  collectIntraPageLinks,
  prepareIntraSearchDepartments,
  resolveDepartmentSearchAction,
  type IntraMenuLink,
  type IntraSearchDepartmentInput,
  type IntraSearchFormInput,
} from '../lib/intraSearchIndex';
import type { SiteSearchHit } from '../lib/searchText';
import {
  complianceFormSearchHref,
  defaultComplianceSearchCards,
  selectVisibleComplianceCards,
  type VisibleComplianceCardFlags,
} from '../lib/complianceVisibleCards';

export type IntraSiteSearchProps = {
  menuLinks: readonly IntraMenuLink[];
  hospitalDepartments: readonly IntraSearchDepartmentInput[];
  scopeDepartmentIds: readonly string[];
  /** Prebuilt form rows for tests. When omitted, forms come from `formVisibility`. */
  forms?: readonly IntraSearchFormInput[];
  formVisibility?: VisibleComplianceCardFlags;
  departmentsLoading: boolean;
  reportComplianceSummaryCategoryId: string | null;
  setReportNurseNotesDeptNavFilterIds: (ids: string[]) => void;
  setReportEthicDeptNavFilterIds: (ids: string[]) => void;
  setTeamDeptNavFilterIds: (ids: string[]) => void;
};

export function IntraSiteSearch({
  menuLinks,
  hospitalDepartments,
  scopeDepartmentIds,
  forms,
  formVisibility,
  departmentsLoading,
  reportComplianceSummaryCategoryId,
  setReportNurseNotesDeptNavFilterIds,
  setReportEthicDeptNavFilterIds,
  setTeamDeptNavFilterIds,
}: IntraSiteSearchProps): React.ReactElement {
  const location = useLocation();
  const navigate = useNavigate();
  const pendingRef = useRef<{ departmentId: string; categoryId: string; kind: 'ethic' | 'nurseNotes' } | null>(null);

  const action = resolveDepartmentSearchAction(location.pathname, location.search);
  const resolvedForms = useMemo(() => {
    if (forms) return forms;
    if (!formVisibility) return [];
    return selectVisibleComplianceCards(defaultComplianceSearchCards(), formVisibility).flatMap((card) => {
      const id = card.categoryDocId ?? '';
      const href = complianceFormSearchHref(id, formVisibility.canViewOrgClimate);
      if (!id || !href) return [];
      return [{ id, label: card.title, href }];
    });
  }, [formVisibility, forms]);
  const hits = useMemo(() => {
    const departments = departmentsLoading
      ? []
      : prepareIntraSearchDepartments(hospitalDepartments, scopeDepartmentIds);
    return buildIntraSearchIndex({
      pages: collectIntraPageLinks(menuLinks),
      departments,
      departmentHref: action.href,
      forms: resolvedForms,
    });
  }, [action.href, departmentsLoading, hospitalDepartments, menuLinks, resolvedForms, scopeDepartmentIds]);

  useEffect(() => {
    const pending = pendingRef.current;
    if (!pending || reportComplianceSummaryCategoryId !== pending.categoryId) return;
    if (pending.kind === 'ethic') {
      setReportEthicDeptNavFilterIds([pending.departmentId]);
    } else {
      setReportNurseNotesDeptNavFilterIds([pending.departmentId]);
    }
    pendingRef.current = null;
  }, [
    reportComplianceSummaryCategoryId,
    setReportEthicDeptNavFilterIds,
    setReportNurseNotesDeptNavFilterIds,
  ]);

  const onActivateHit = (hit: SiteSearchHit): void => {
    if (!hit.departmentId) return;
    const next = resolveDepartmentSearchAction(location.pathname, location.search);
    navigate(next.href);
    if (next.kind === 'team') {
      setTeamDeptNavFilterIds([hit.departmentId]);
      pendingRef.current = null;
      return;
    }
    if (reportComplianceSummaryCategoryId === next.waitForCategoryId) {
      if (next.kind === 'ethic') setReportEthicDeptNavFilterIds([hit.departmentId]);
      else setReportNurseNotesDeptNavFilterIds([hit.departmentId]);
      pendingRef.current = null;
      return;
    }
    pendingRef.current = {
      departmentId: hit.departmentId,
      categoryId: next.waitForCategoryId ?? '',
      kind: next.kind === 'ethic' ? 'ethic' : 'nurseNotes',
    };
  };

  return (
    <SiteSearchCombobox
      hits={hits}
      placeholder="ค้นหาหน้าหรือหน่วยงาน"
      ariaLabel="ค้นหาหน้า หน่วยงาน หรือแบบฟอร์ม"
      departmentsLoading={departmentsLoading}
      onActivateHit={onActivateHit}
    />
  );
}
